import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";

type Readiness = {
  ok: boolean; mode: string; passed: number; total: number;
  webhook_secret_present: boolean;
  last_processed: { type?: string; processed_at?: string } | null;
  totals: { total_24h: number; processed_24h: number; failed_24h: number; live_24h: number };
};

type EmailMetrics = { queued: number; sending: number; sent: number; failed: number };

type SmokeRun = { ok: boolean; passed: number; total: number; finished_at?: string };

const Badge = ({ ok, label }: { ok: boolean; label: string }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-mono uppercase border ${
    ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
       : "border-red-500/40 bg-red-500/10 text-red-400"
  }`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {label}
  </span>
);

const Card = ({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) => (
  <div className="bg-panel border border-border p-6">
    <div className="flex items-center justify-between mb-4 gap-2">
      <h2 className="font-display tracking-widest text-sm">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

export default function AdminHealth() {
  const [loading, setLoading] = useState(true);
  const [stripe, setStripe] = useState<Readiness | null>(null);
  const [email, setEmail] = useState<EmailMetrics | null>(null);
  const [smoke, setSmoke] = useState<SmokeRun | null>(null);
  const [cron, setCron] = useState<{ name: string; last_run: string | null; ok: boolean }[]>([]);

  const load = async () => {
    setLoading(true);
    const safe = async <T,>(p: PromiseLike<T>): Promise<T | { data: any }> => {
      try { return await p; } catch { return { data: null }; }
    };
    const [readiness, emailQueue, smokeHist, cronJobs] = await Promise.all([
      safe(supabase.functions.invoke("stripe-readiness")),
      safe((supabase.from as any)("email_queue").select("status")),
      safe((supabase.from as any)("attribution_test_runs")
        .select("passed,total,finished_at,ok")
        .order("finished_at", { ascending: false }).limit(1).maybeSingle()),
      safe(supabase.from("email_send_log").select("created_at").order("created_at", { ascending: false }).limit(1)),
    ]);

    setStripe((readiness as any)?.data ?? null);

    const rows = ((emailQueue as any)?.data ?? []) as { status: string }[];
    setEmail({
      queued: rows.filter(r => r.status === "queued").length,
      sending: rows.filter(r => r.status === "sending").length,
      sent: rows.filter(r => r.status === "sent").length,
      failed: rows.filter(r => r.status === "failed").length,
    });

    const sr = (smokeHist as any)?.data;
    setSmoke(sr ? { ok: !!sr.ok, passed: sr.passed ?? 0, total: sr.total ?? 0, finished_at: sr.finished_at } : null);

    const last = ((cronJobs as any)?.data?.[0]?.created_at) ?? null;
    setCron([
      { name: "email-queue worker", last_run: last, ok: !!last && (Date.now() - new Date(last).getTime() < 60 * 60 * 1000) },
      { name: "campaign worker", last_run: last, ok: !!last },
      { name: "cleanup-pending-bookings", last_run: last, ok: !!last },
    ]);

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <AdminShell title="HEALTH" actions={
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const id = Sentry.captureException(
              new Error(`Sentry sourcemap test @ ${new Date().toISOString()}`)
            );
            alert(`Sent test error to Sentry. Event ID: ${id}`);
          }}
        >
          Send test error to Sentry
        </Button>
        <Button variant="outline" onClick={load} disabled={loading} size="sm">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Refresh
        </Button>
      </div>
    }>
      {loading && !stripe ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="STRIPE WEBHOOKS" action={
            <Link to="/admin/stripe/webhook-status" className="text-xs font-mono text-primary hover:underline">Open →</Link>
          }>
            {stripe ? (
              <div className="space-y-2 text-sm font-body">
                <div className="flex items-center gap-2">
                  <Badge ok={stripe.mode === "live"} label={stripe.mode.toUpperCase()} />
                  <Badge ok={stripe.webhook_secret_present} label="webhook secret" />
                  <Badge ok={stripe.totals.failed_24h === 0} label={`${stripe.totals.failed_24h} failed/24h`} />
                </div>
                <p className="text-xs text-muted-foreground font-mono">
                  {stripe.totals.processed_24h} processed · {stripe.totals.live_24h} live events (24h)
                </p>
                {stripe.last_processed && (
                  <p className="text-xs text-muted-foreground">
                    Last: <span className="font-mono">{stripe.last_processed.type}</span>{" "}
                    {stripe.last_processed.processed_at && new Date(stripe.last_processed.processed_at).toLocaleString()}
                  </p>
                )}
              </div>
            ) : <p className="text-sm text-muted-foreground">Unavailable.</p>}
          </Card>

          <Card title="EMAIL QUEUE" action={
            <Link to="/admin/emails" className="text-xs font-mono text-primary hover:underline">Open →</Link>
          }>
            {email ? (
              <div className="grid grid-cols-4 gap-3 text-center">
                <div><div className="text-2xl font-display text-primary">{email.queued}</div><div className="text-[10px] font-mono uppercase text-muted-foreground">Queued</div></div>
                <div><div className="text-2xl font-display">{email.sending}</div><div className="text-[10px] font-mono uppercase text-muted-foreground">Sending</div></div>
                <div><div className="text-2xl font-display text-emerald-400">{email.sent}</div><div className="text-[10px] font-mono uppercase text-muted-foreground">Sent</div></div>
                <div><div className="text-2xl font-display text-red-400">{email.failed}</div><div className="text-[10px] font-mono uppercase text-muted-foreground">Failed</div></div>
              </div>
            ) : <p className="text-sm text-muted-foreground">No data.</p>}
          </Card>

          <Card title="CRON / WORKERS">
            <ul className="space-y-2 text-sm font-body">
              {cron.map((j) => (
                <li key={j.name} className="flex items-center justify-between border-b border-border pb-2 last:border-0">
                  <div className="flex items-center gap-2">
                    {j.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-yellow-400" />}
                    <span className="font-mono text-xs">{j.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{j.last_run ? new Date(j.last_run).toLocaleString() : "—"}</span>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="PROD SMOKE TEST" action={
            <Link to="/admin/prod-smoke-test" className="text-xs font-mono text-primary hover:underline">Run →</Link>
          }>
            {smoke ? (
              <div className="space-y-2 text-sm font-body">
                <Badge ok={smoke.ok} label={`${smoke.passed}/${smoke.total} passed`} />
                {smoke.finished_at && (
                  <p className="text-xs text-muted-foreground">Last run: {new Date(smoke.finished_at).toLocaleString()}</p>
                )}
              </div>
            ) : <p className="text-sm text-muted-foreground">No runs recorded yet.</p>}
          </Card>
        </div>
      )}
    </AdminShell>
  );
}